/**
 * Columns for a collection's rows — derived from the rows themselves.
 *
 * The explorer reads every project-scoped collection through ONE endpoint, so
 * there is no hand-written column set to reach for: a fixed map would need ~40
 * entries, would drift the moment a field was added, and would have nothing at
 * all to say about a collection nobody had got round to listing. The rows know
 * their own shape, so the shape is read off them.
 *
 * What it produces is deliberately close to the product's real grids:
 *  · the name-ish field leads, the way every list screen leads with its name;
 *  · `createdAt`/`updatedAt` trail at the right, where those screens put them;
 *  · identity, scoping and the audit trail are dropped — `_id` and `appId` are
 *    plumbing, and PROJECT is never a column here because the chart already
 *    said which project these rows belong to;
 *  · a handful of columns, not forty. The full document is one click away, so a
 *    grid that scrolls sideways past the fold buys nothing.
 */
import { formatDate } from 'sharedUi/dateUtils';
import type { SaasColumn } from '../../components/common/saas-data-table';

type Row = Record<string, unknown>;

/** Plumbing, not data. `appId` in particular: the Project column the real grids
 *  carry is meaningless inside a chart branch that already names the project. */
const SKIP = new Set([
  '_id', 'id', '__v', 'appId', 'isDelete', 'isDeleted', 'deletedAt', 'deletedBy',
  'createdBy', 'updatedBy', 'createdIp', 'updatedIp', 'createdIP', 'updatedIP',
]);

/** What a list screen leads with, best first. */
const LEAD = [
  'displayName', 'name', 'jobName', 'title', 'label', 'key', 'code', 'errorCode',
  'logCode', 'unitCode', 'feedbackKey', 'agentCode', 'suiteCode', 'caseCode',
  'promptId', 'scheduleName', 'ragCode', 'pipelineCode', 'userId', 'email',
  'description',
];

/** Timestamps belong at the right-hand end, as they do on every other grid. */
const TRAIL = ['createdAt', 'updatedAt'];

const MAX_COLUMNS = 7;

/** Words that are read out, not capitalised: "Model Id" is wrong, "Model ID" is
 *  what the screen next door says. */
const ACRONYMS: Record<string, string> = {
  id: 'ID', ids: 'IDs', url: 'URL', uri: 'URI', api: 'API', ai: 'AI', ip: 'IP',
  db: 'DB', ui: 'UI', sme: 'SME', rag: 'RAG', otp: 'OTP', mpin: 'MPIN',
  sla: 'SLA', json: 'JSON', http: 'HTTP', sms: 'SMS', pdf: 'PDF',
};

/** `modelId` → "Model ID", `provider_type` → "Provider Type". */
export const humanize = (field: string): string =>
  field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_.-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => ACRONYMS[w.toLowerCase()] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/** Mongo hands back ISO strings; a grid cell wants a date. */
const ISO = /^\d{4}-\d{2}-\d{2}T/;

/** One cell. Nested values are counted rather than dumped — the full document is
 *  a click away, and a serialised object in a table row destroys the row. */
export const cell = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '-';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.length ? `${v.length} item${v.length === 1 ? '' : 's'}` : '-';
  if (typeof v === 'object') return `${Object.keys(v as object).length} fields`;
  const s = String(v);
  return ISO.test(s) ? formatDate(s) : s;
};

/**
 * The columns for one page of rows.
 *
 * Every row on the page is scanned, not just the first: documents in a Mongo
 * collection do not have to agree on their fields, and keying off row 0 alone
 * hides a column for everyone whose first record happens to be sparse. A field
 * that is empty in all of them is dropped — a column of dashes is noise.
 *
 * None of these are hideable, and that is load-bearing: the table's column
 * toggle is seeded once at mount, and at mount this dialog has no rows yet. A
 * hideable column arriving with the data would land outside that seed and never
 * be drawn.
 */
export function deriveColumns(rows: Row[]): SaasColumn<Row>[] {
  const seen: string[] = [];
  const populated = new Set<string>();
  const nested = new Set<string>();
  rows.forEach((r) => {
    Object.entries(r).forEach(([k, v]) => {
      if (SKIP.has(k)) return;
      if (!seen.includes(k)) seen.push(k);
      if (v !== null && v !== undefined && v !== '') populated.add(k);
      if (v !== null && typeof v === 'object') nested.add(k);
    });
  });

  const fields = seen.filter((f) => populated.has(f));
  // Fixed before the sort: reading a position out of the array being sorted
  // makes the comparator answer differently each time it is asked.
  const order = new Map(fields.map((f, i) => [f, i]));
  const rank = (f: string): number => {
    const lead = LEAD.indexOf(f);
    if (lead >= 0) return lead;
    const trail = TRAIL.indexOf(f);
    if (trail >= 0) return 2000 + trail;
    // A nested value reduces to "3 fields" in a cell, which tells the reader
    // nothing. It still earns a column if there is room, but only once every
    // scalar has had one — the native list APIs return several of these.
    return (nested.has(f) ? 1500 : 1000) + (order.get(f) ?? 0);
  };

  return [...fields]
    .sort((a, b) => rank(a) - rank(b))
    .slice(0, MAX_COLUMNS)
    .map((f) => ({
      field: f,
      header: humanize(f),
      hideable: false,
      body: (row: Row) => cell(row[f]),
    }));
}
